import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, UtensilsCrossed } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import AuthGate from "@/components/AuthGate";

const CATS = ["PRIMI", "SECONDI", "CONTORNI"];
const fieldCls = "w-full bg-cream border border-charred/20 rounded-md px-4 py-3 text-charred placeholder:text-charred/40 focus:outline-none focus:ring-2 focus:ring-terracotta/40";

function useNoIndex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);
}

function DishRow({ dish, onEdit, onToggle, onMove, onDelete }) {
  return (
    <div className={`bg-cream border border-charred/10 rounded-lg p-4 ${dish.available ? "" : "opacity-70"}`} data-testid={`dish-row-${dish.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className={`font-serif text-xl leading-snug text-charred ${dish.available ? "" : "line-through decoration-terracotta/50"}`}>{dish.name}</p>
          <div className="mt-1 flex items-center gap-3 text-xs">
            {dish.price && <span className="font-semibold text-terracotta">{dish.price}</span>}
            <span className={`uppercase tracking-[0.15em] font-semibold ${dish.available ? "text-olive-dark" : "text-terracotta"}`}>
              {dish.available ? "Disponibile" : "Esaurito"}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={() => onToggle(dish)} data-testid={`dish-toggle-${dish.id}`}
          className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${dish.available ? "bg-charred/10 text-charred hover:bg-charred/20" : "bg-olive/20 text-olive-dark hover:bg-olive/30"}`}>
          {dish.available ? "Segna esaurito" : "Segna disponibile"}
        </button>
        <button onClick={() => onEdit(dish)} data-testid={`dish-edit-${dish.id}`}
          className="rounded-full px-4 py-2 text-xs font-semibold bg-charred/10 text-charred hover:bg-charred/20 transition-colors inline-flex items-center gap-1.5">
          <Pencil size={12} /> Modifica
        </button>
        <select
          value={dish.category}
          onChange={(e) => onMove(dish, e.target.value)}
          data-testid={`dish-move-${dish.id}`}
          className="rounded-full px-3 py-2 text-xs font-semibold bg-charred/10 text-charred hover:bg-charred/20 transition-colors cursor-pointer"
          aria-label="Sposta categoria"
        >
          {CATS.map((c) => <option key={c} value={c}>{c === dish.category ? `In ${c}` : `Sposta in ${c}`}</option>)}
        </select>
        <button onClick={() => onDelete(dish)} data-testid={`dish-delete-${dish.id}`}
          className="rounded-full px-4 py-2 text-xs font-semibold bg-terracotta/10 text-terracotta-dark hover:bg-terracotta/20 transition-colors inline-flex items-center gap-1.5">
          <Trash2 size={12} /> Elimina
        </button>
      </div>
    </div>
  );
}

function JobsSection() {
  const [active, setActive] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/jobs").then((r) => setActive(r.data.active)).catch(() => setActive(true));
  }, []);

  const update = async (v) => {
    setSaving(true);
    try {
      await api.put("/admin/settings/jobs", { active: v });
      setActive(v);
      toast.success(v ? "Candidature ATTIVE: la pagina Lavora con noi è visibile sul sito" : "Candidature NON ATTIVE: la sezione è nascosta dal sito");
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (active === null) return null;

  return (
    <section className="mt-12 bg-sand border border-charred/10 rounded-lg p-6" data-testid="jobs-management">
      <h2 className="font-serif text-2xl text-charred">GESTIONE "LAVORA CON NOI"</h2>
      <p className="mt-1 text-sm text-charred/60">
        Quando è attivo, il link "Lavora con noi" compare nel footer e il modulo accetta candidature. Quando è disattivo, la sezione sparisce dal sito e non viene raccolto alcun dato.
      </p>
      <div className="mt-5 flex items-center gap-4 flex-wrap">
        <span className="text-xs uppercase tracking-[0.15em] text-charred/50">Candidature:</span>
        <div className="inline-flex rounded-full border border-charred/20 overflow-hidden" data-testid="jobs-toggle">
          <button
            onClick={() => update(true)}
            disabled={saving}
            data-testid="jobs-active-btn"
            className={`px-6 py-2.5 text-sm font-semibold transition-colors ${active ? "bg-olive-dark text-cream" : "bg-cream text-charred/60 hover:bg-sand"}`}
          >
            ATTIVE
          </button>
          <button
            onClick={() => update(false)}
            disabled={saving}
            data-testid="jobs-inactive-btn"
            className={`px-6 py-2.5 text-sm font-semibold transition-colors ${!active ? "bg-terracotta text-cream" : "bg-cream text-charred/60 hover:bg-sand"}`}
          >
            NON ATTIVE
          </button>
        </div>
      </div>
    </section>
  );
}

export default function GestioneMenu() {
  const [user, setUser] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dishes, setDishes] = useState([]);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);

  useNoIndex();

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/dishes", { params: { date } });
      setDishes(data);
    } catch {
      toast.error("Errore di caricamento del menù");
    }
  }, [date]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const save = async () => {
    if (!editor.name.trim()) {
      toast.error("Inserisci il nome del piatto");
      return;
    }
    setSaving(true);
    try {
      const payload = { date, name: editor.name, category: editor.category, price: editor.price, available: editor.available };
      if (editor.id) await api.put(`/admin/dishes/${editor.id}`, payload);
      else await api.post("/admin/dishes", payload);
      toast.success(editor.id ? "Piatto aggiornato sul sito" : "Piatto aggiunto al menù di oggi");
      setEditor(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (d) => {
    try {
      await api.put(`/admin/dishes/${d.id}`, { available: !d.available });
      load();
    } catch { toast.error("Errore"); }
  };

  const move = async (d, category) => {
    if (category === d.category) return;
    try {
      await api.put(`/admin/dishes/${d.id}`, { category });
      toast.success(`"${d.name}" spostato in ${category}`);
      load();
    } catch { toast.error("Errore"); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Eliminare definitivamente "${d.name}"?`)) return;
    try {
      await api.delete(`/admin/dishes/${d.id}`);
      toast.success("Piatto eliminato");
      load();
    } catch { toast.error("Errore"); }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  if (user === null) return <div className="min-h-screen bg-cream grid place-items-center text-charred/50">Caricamento…</div>;
  if (user === false) return <AuthGate subtitle="Gestione Menù — accesso riservato" onLogin={setUser} />;

  const dateLabel = new Date(`${date}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="min-h-screen bg-cream pb-24" data-testid="gestione-menu-page">
      <header className="bg-charred text-cream sticky top-0 z-30">
        <div className="max-w-[900px] mx-auto px-5 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <UtensilsCrossed size={20} className="text-terracotta-light shrink-0" />
            <p className="font-serif text-xl md:text-2xl truncate">GESTIONE MENÙ SAPORÉ</p>
          </div>
          <button onClick={logout} data-testid="gestione-logout-btn"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-cream/70 hover:text-cream shrink-0">
            <LogOut size={14} /> Esci
          </button>
        </div>
      </header>

      <div className="max-w-[900px] mx-auto px-5 pt-6">
        <label className="text-xs uppercase tracking-[0.2em] text-charred/50 block mb-2">Data del menù</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`${fieldCls} max-w-[220px]`} data-testid="gestione-date-input" />
        <p className="mt-2 text-sm text-charred/60 capitalize" data-testid="gestione-date-label">{dateLabel}</p>

        {editor && (
          <div className="mt-6 bg-sand border-2 border-terracotta/40 rounded-lg p-5" data-testid="dish-editor">
            <p className="font-serif text-xl text-charred">{editor.id ? "Modifica piatto" : "Nuovo piatto"}</p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.15em] text-charred/50 block mb-1.5">Nome piatto *</label>
                <input value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })} className={fieldCls} placeholder="Es. Orecchiette al sugo" data-testid="dish-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-charred/50 block mb-1.5">Categoria</label>
                  <select value={editor.category} onChange={(e) => setEditor({ ...editor, category: e.target.value })} className={fieldCls} data-testid="dish-category-select">
                    {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.15em] text-charred/50 block mb-1.5">Prezzo (facoltativo)</label>
                  <input value={editor.price} onChange={(e) => setEditor({ ...editor, price: e.target.value })} className={fieldCls} placeholder="Es. € 7,00" data-testid="dish-price-input" />
                </div>
              </div>
              <label className="flex items-center gap-3 text-sm text-charred/80 cursor-pointer">
                <input type="checkbox" checked={editor.available} onChange={(e) => setEditor({ ...editor, available: e.target.checked })}
                  className="w-5 h-5 accent-[#7B8A5A]" data-testid="dish-available-checkbox" />
                Disponibile oggi
              </label>
              <div className="flex gap-3">
                <button onClick={save} disabled={saving} data-testid="dish-save-btn"
                  className="flex-1 rounded-full bg-terracotta text-cream py-3 text-sm font-semibold hover:bg-terracotta-dark transition-colors disabled:opacity-60">
                  {saving ? "Salvataggio…" : "Salva"}
                </button>
                <button onClick={() => setEditor(null)} data-testid="dish-cancel-btn"
                  className="rounded-full border border-charred/30 text-charred px-6 py-3 text-sm font-semibold hover:bg-charred hover:text-cream transition-colors">
                  Annulla
                </button>
              </div>
            </div>
          </div>
        )}

        {CATS.map((cat) => {
          const items = dishes.filter((d) => d.category === cat);
          return (
            <section key={cat} className="mt-10" data-testid={`gestione-cat-${cat.toLowerCase()}`}>
              <div className="flex items-center justify-between border-b-2 border-charred/15 pb-2">
                <h2 className="font-serif text-2xl text-charred">{cat}</h2>
                <span className="text-xs text-charred/50">{items.length} piatti</span>
              </div>
              <div className="mt-4 space-y-3">
                {items.length === 0 && <p className="text-sm text-charred/40 italic">Nessun piatto in questa categoria.</p>}
                {items.map((d) => (
                  <DishRow key={d.id} dish={d}
                    onEdit={(x) => setEditor({ id: x.id, name: x.name, category: x.category, price: x.price || "", available: x.available })}
                    onToggle={toggle} onMove={move} onDelete={remove} />
                ))}
              </div>
              <button
                onClick={() => setEditor({ id: null, name: "", category: cat, price: "", available: true })}
                data-testid={`add-dish-${cat.toLowerCase()}`}
                className="mt-4 w-full rounded-lg border-2 border-dashed border-terracotta/40 text-terracotta py-3.5 text-sm font-semibold hover:bg-terracotta/5 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Plus size={16} /> Aggiungi piatto
              </button>
            </section>
          );
        })}

        <p className="mt-12 text-xs text-charred/40 text-center">Ogni modifica è visibile subito nella sezione "Piatti del giorno" del sito pubblico.</p>

        <JobsSection />
      </div>
    </div>
  );
}
